import * as React from 'react';

import Modal from 'antd/lib/modal';
import {Field, Form, Formik, FormikProps} from 'formik';
import * as Yup from 'yup';

import {phoneRegexp} from '@common/react/utils/validationHelpers';
import CustomFile from '@common/react/components/Forms/Files/File';
import {FileInterface, FileType} from '@common/typescript/objects/FileInterface';

import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import FormikField from '@app/components/Forms/CustomFormikField/CustomFormikField';
import {CustomPhoneControl} from '@app/components/Forms/CustomPhoneControl/CustomPhoneControl';
import CustomSelect from '@app/components/Forms/CustomSelect/CustomSelect';
import AttachmentPreview from '@app/components/UI/Attachment/AttachmentPreview';
import {onFileUpload} from '@app/components/LocalCommon/Utils/onFileUpload';
import {stringBeautifyValidator} from '@app/components/LocalCommon/Utils/Validators';
import {rest} from '@app/components/Api';

import '@app/scss/localCommon/components/attachment.scss';
import '@app/scss/components/studentExternshipModal.scss';

const validationSchema = Yup.object({
	firstName: stringBeautifyValidator('First Name is a required field'),
	lastName: stringBeautifyValidator('Last Name is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	phone: Yup.string().required('Phone Number is a required field').matches(phoneRegexp, 'Invalid Phone Number'),
	vetSchool: stringBeautifyValidator('Veterinarian School is a required field'),
	graduationDate: stringBeautifyValidator('Graduation Date is a required field'),
	source: stringBeautifyValidator('How did you hear about us is a required field'),
	dateRange: stringBeautifyValidator('Date Range is a required field'),
});

const FormSuccess = () => {
	React.useEffect(() => {
		window.scrollTo(0, 0);
		document.querySelector('#react-app')?.scrollTo(0, 0);
	}, []);

	return (
		<div className="externship-inquiry-form__success">
			<h3>Thank you!</h3>
			<h5>
				We’ve received your information and will be in touch as soon as possible.
			</h5>
		</div>
	);
};

const StudentExternshipModal = ({modalVisible, setModalVisible, formSuccess, setFormSuccess}) => {
	const [attachment, setAttachment] = React.useState<FileInterface[]>([]);
	const [sending, setSending] = React.useState(false);

	const maxFile = 5000000;
	const imgExtension = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
	const maxImgCount = 1;
	const sourcesListOptions: { value: number, label: string }[] = [
		{value: 0, label: 'Conference'},
		{value: 1, label: 'VBMA talk with Dr. Dani or Dr. Mary'},
		{value: 2, label: 'CVM Event'},
		{value: 3, label: 'Previous Lap of Love client'},
		{value: 4, label: 'Other'}
	];

	return(
		<Modal
			wrapClassName="modal-wrap job-inquiry externship-inquiry"
			visible={modalVisible}
			onOk={() => setModalVisible(false)}
			onCancel={() => setModalVisible(false)}
			width={1037}
			centered
		>
			<div className="job-inquiry__content">
				<div className="modal-wrap__container">
					{
						!formSuccess.success ?

					<Formik
						onSubmit={(values, formikActions) => {
							setSending(true);
							rest.post('v1/externship', values)
								.then((res) => {
									console.log(values, 'FORM_VALUES');
									setSending(false);
									formikActions.resetForm();
									setAttachment([]);
									setFormSuccess({success: true, error: ''});
								});
						}}
						initialValues={{
							firstName: '',
							lastName: '',
							email: '',
							phone: '',
							vetSchool: '',
							graduationDate: '',
							source: '',
							dateRange: '',
							notes: '',
							filePath: []
						}}
						validationSchema={validationSchema}
						render={(formikBag: FormikProps<any>) => {
							return (
								<Form className="job-inquiry__form externship-inquiry__form">
									<h3 className="job-inquiry-title">
										Externship inquiry
									</h3>
									<div className="form-group">
										<CustomInput
											type="text"
											name="firstName"
											placeholder="First Name*"
											formikBag={formikBag}
										/>
										<CustomInput
											type="text"
											name="lastName"
											placeholder="Last Name*"
											formikBag={formikBag}
										/>
										<CustomInput
											name="email"
											placeholder="Email Address*"
											formikBag={formikBag}
											removeSpace={true}
										/>
										<div className="phone">
											<FormikField
												title="Phone Number*"
												fieldName="phone"
												render={fieldProps =>
													<CustomPhoneControl
														placeholder="Phone Number*"
														fieldProps={fieldProps}
													/>
												}
											/>
										</div>
										<CustomInput
											type="text"
											name="vetSchool"
											placeholder="Vet School*"
											formikBag={formikBag}
										/>
										<CustomInput
											type="text"
											name="graduationDate"
											placeholder="Graduation Date*"
											formikBag={formikBag}
										/>

									</div>
									<div className="form-group form-group_full-width">
										<CustomSelect
											name="source"
											placeholder="How did you hear about Lap of Love?*"
											onSelect={(value, option) => {
												formikBag.setFieldValue('source', value);
											}}
											onChange={(value, option) => {
												formikBag.setFieldValue('source', value);
											}}
											onSearch={(value, option) => {
												formikBag.setFieldValue('source', value);
											}}
											options={sourcesListOptions}
											showSearch={false}
											onFocus={() => {}}
											transform={false}
											formikBag={formikBag}
										/>
									</div>
									<div className="form-group form-group_textarea">
											<CustomInput
												component={'textarea'}
												name="dateRange"
												placeholder="What date range are you interested in completing your externship?*"
												formikBag={formikBag}
											/>
											<div
												className={!formikBag.values['dateRange'] ? 'reminder reminder-visible' : 'reminder reminder-hidden'}
											>
												(Reminder: you must be a 3rd or 4th year vet student when completing the externship)
											</div>

										<CustomInput
											component={'textarea'}
											name="notes"
											placeholder="Share with us any notes or additional questions you have about the program"
											formikBag={formikBag}
										/>
									</div>
									<div className="form-group_full-width">
										<Field
											name="filePath"
											render={(field) => {
												return (
													<div className="attachment modal-wrap">
														<h4 className="attachment-title">
															Attach a resume
														</h4>
														<AttachmentPreview
															attachment={attachment}
															setAttachment={setAttachment}
															formikBag={formikBag}
														/>
														<CustomFile
															update={data => onFileUpload(
																data,
																field,
																formikBag,
																'filePath',
																maxFile,
																imgExtension,
																attachment,
																maxImgCount,
																setAttachment,
																'Resume must be in PDF format',
															)}
															type="jobInquiry"
															fileType={FileType.File}
															noRelation={true}
															asButton buttonClassName="btn btn-secondary"
															buttonCaption="Upload a file"
															multiple={true}
														/>
														{
															field.form.errors['filePath'] && field.form.touched['filePath'] ?
																<div
																	className="custom-input__error"> {field.form.errors['filePath']}</div> : null
														}
													</div>
												);
											}}
										/>
									</div>

									<div className="form-group form-group_submit">
										<button
											type="submit"
											className="btn btn-md btn-primary"
											disabled={sending}
										>
											Submit your application
										</button>
										{
											Object.values(formikBag.errors).some(el => el) &&
											formikBag.submitCount > 0 &&
											<div className="submit-validate-error">Please fill in all required fields marked with an asterisk</div>

										}
									</div>
									{
										sending ?
											<div className="lds-ring">
												<div/>
												<div/>
												<div/>
												<div/>
											</div>
											: null
									}

								</Form>
							);
						}}
					/> : <FormSuccess/>
				}
				</div>
			</div>
		</Modal>
	);
};

export default StudentExternshipModal;